import React, { useState, useEffect, useRef } from "react";
import { Card, ListGroup, Form, Dropdown } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import ResourceViewerModal from "../modals/ResourceViewerModal";
import styles from "./Card.module.css";
import { fetchProvider } from "../services/providerService";
import { useStore, useSelector } from "react-redux";
import { Overlay, Tooltip } from "react-bootstrap";
import DoctorFemaleIcon from "../assets/Doctor_Female_96.png";
import DoctorMaleIcon from "../assets/Doctor_Male_96.png";
import { resolveName } from "../utils/messageUtils";

function ProviderCard({
  title,
  providerData,
  showSearch,
  inContext,
  onSelectProvider,
  fhirClient
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedProviders, setSearchedProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(providerData || null);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalProviderDetails, setModalProviderDetails] = useState(null);
  const handleModalClose = () => setShowModal(false);
  const store = useStore();
  const providers = useSelector(state => state.providers);
  const [showTooltip, setShowTooltip] = useState(false);
  const iconRef = useRef(null);

  useEffect(() => {
    if (providerData?.name?.[0]) {
      const name = resolveName(providerData, store);
      setSearchTerm(name);
    }
  }, [providerData]);

  const handleModalOpen = async () => {
    const providerId = displayProvider?.id;

    if (!providerId) {
      console.warn("Provider ID not available");
      setModalProviderDetails(null);
      setShowModal(true);
      return;
    }

    let provider = providers[providerId];

    if (!provider) {
      try {
        provider = await fetchProvider(providerId, fhirClient, store);
      } catch (err) {
        console.error("Failed to fetch provider via service", err);
      }
    }

    setModalProviderDetails(provider || null);
    setShowModal(true);
  };

  async function handleSearchByName() {
    if (!searchTerm.trim()) return setError("Please enter a provider name.");
    if (!fhirClient) return setError("FHIR Client is not initialized.");

    try {
      const searchResults = await fhirClient.request(`Practitioner?name=${encodeURIComponent(searchTerm)}`);
      const found = searchResults.entry?.map(e => e.resource) || [];
      setSearchedProviders(found);
      setError(found.length ? "" : "No providers found.");
    } catch (err) {
      console.error("Error fetching provider data:", err);
      setError("Error fetching provider data.");
    }
  }

  function handleSelectProvider(provider) {
    const fullName = provider.name?.[0]?.given?.join(" ") + " " + provider.name?.[0]?.family;
    setSearchTerm(fullName);
    setSelectedProvider(provider);
    setSearchedProviders([]);
    if (onSelectProvider) {
      onSelectProvider(provider);
    }
  }

  const displayProvider = selectedProvider || providerData || {
    name: [{ given: [""], family: "" }],
    resourceType: "N/A",
    gender: "N/A",
    active: "Unknown",
    qualification: [],
    address: [{ line: [""], city: "", state: "", postalCode: "" }],
    telecom: [{ system: "phone", value: "N/A" }]
  };

  const providerAddress = displayProvider.address?.[0];
  const fullAddress = [
    ...(providerAddress?.line || []),
    providerAddress?.city,
    providerAddress?.state,
    providerAddress?.postalCode
  ].filter(Boolean).join(", ");

  const googleMapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;

  // ✅ Select gender-specific icon
  const gender = displayProvider.gender?.toLowerCase() ?? 'Unknown';
  const doctorIcon = gender === "female" ? DoctorFemaleIcon : DoctorMaleIcon;

  return (
    <>
      <Card className={styles.providerCard}>
        <Card.Header
          className={
            inContext
              ? styles.inContextCardHeader
              : styles.cardHeader
          }
          style={{ cursor: "pointer" }}
          onClick={handleModalOpen}
        >
          <div className="d-flex justify-content-between align-items-center w-100">

            {/* ---- TITLE ---- */}
            <h6 className="mb-0">
              <strong>{title}</strong>
            </h6>

            {/* ---- PROVIDER ICON + TOOLTIP ---- */}
            <span
              ref={iconRef}
              className="ms-2 d-inline-flex align-items-center"
              onMouseEnter={(e) => {
                e.stopPropagation();
                setShowTooltip(true);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                setShowTooltip(false);
              }}
              onClick={(e) => {
                e.stopPropagation(); // icon click shouldn't double-trigger the header click
                handleModalOpen();
              }}
            >
              <img
                src={doctorIcon}
                alt="Doctor Icon"
                className={styles.fhirIcon}
              />
            </span>

            <Overlay
              target={iconRef.current}
              show={showTooltip}
              placement="bottom"
            >
              {(props) => (
                <Tooltip id="provider-tooltip" {...props}>
                  View provider details
                </Tooltip>
              )}
            </Overlay>

          </div>
        </Card.Header>



        <Card.Body>
          {error && <p className={styles.errorText}>{error}</p>}

          <ListGroup variant="flush">
            <ListGroup.Item className={`d-flex align-items-center ${styles.listItem}`}>
              <strong>Name:&nbsp;</strong>
              {showSearch ? (
                <div className={styles.searchContainer}>
                  <Form.Control
                    type="text"
                    placeholder="Find a Provider"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearchByName();
                      }
                    }}
                    className="me-2"
                  />
                  <FaSearch
                    className={styles.searchIcon}
                    onClick={handleSearchByName}
                  />
                  {searchedProviders.length > 0 && (
                    <Dropdown.Menu show className={styles.searchDropdown}>
                      {searchedProviders.map((provider) => (
                        <Dropdown.Item key={provider.id} onClick={() => handleSelectProvider(provider)}>
                          {provider.name?.[0]?.given?.join(" ")} {provider.name?.[0]?.family} :{" "}
                          {provider.address?.[0]?.city}, {provider.address?.[0]?.state}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  )}
                </div>
              ) : (
                <>
                  {displayProvider.name?.[0] &&
                    (displayProvider.name[0].given?.[0]?.trim() || displayProvider.name[0].family?.trim())
                    ? `Dr. ${displayProvider.name[0].given.join(" ")} ${displayProvider.name[0].family}`
                    : "Unknown"}
                </>
              )}
            </ListGroup.Item>

            <ListGroup.Item className={styles.listItem}><strong>Provider Type:</strong> {displayProvider.resourceType}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Gender:</strong> {gender}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Active:</strong> {displayProvider.active === "Unknown" ? "Unknown" : displayProvider.active ? "Yes" : "No"}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}><strong>Qualifications:</strong> {displayProvider.qualification?.map(q => q.code.text).join(", ") || "N/A"}</ListGroup.Item>
            <ListGroup.Item className={styles.listItem}>
              <strong>Address:</strong> {fullAddress || "N/A"}
              {googleMapsUrl && (
                <div className="mt-1">
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">View on Google Maps</a>
                </div>
              )}
            </ListGroup.Item>
            <ListGroup.Item className={styles.listItem}>
              <strong>Phone Number:</strong> {displayProvider.telecom?.find(t => t.system === "phone")?.value}
            </ListGroup.Item>
          </ListGroup>
        </Card.Body>
      </Card>


      <ResourceViewerModal
        show={showModal}
        onClose={handleModalClose}
        resource={modalProviderDetails}
      />
    </>
  );
}

export default ProviderCard;